// Sugalarm — cycle 433: the wake handler and the bounded reseed, as two
// small pieces with no React in them, so the rules can be driven under node
// against promises that never settle.
//
// ⚠ WHY THIS FILE EXISTS. Until this cycle both rules lived inline in
// `useGlucoseSocket`'s wake effect, where the only way to exercise them was
// to mount a component. They were therefore never tested, and what they
// actually did was this:
//
//     const handleWake = async () => {
//       if (wakeInFlightRef.current) return;      // ← the whole bug
//       ...
//       try { sock.reopen("wake"); await reseedFromRest(signal); }
//       finally { wakeInFlightRef.current = false; }
//     };
//
// The marker could only be cleared once the `await` settled. iOS freezes a
// fetch mid-flight when the app backgrounds and the promise can pend for
// minutes after the app returns, so every wake that arrived in that window
// was discarded — the RECONNECT along with the reseed. On 2026-09-13 the
// 23:14:50 wake reached the server at 23:23:06 showing no reopen, no reseed
// and no socket, and for 88 seconds of foreground the phone displayed 39
// while the member's own receiver read 52.
//
// Two rules, both here:
//   1. THE RECONNECT IS NEVER BEHIND A GUARD. It is one synchronous call
//      that cannot pend; only the REST reseed can.
//   2. THE RESEED IS BOUNDED, COALESCING, AND ALWAYS RELEASES. The bound
//      is a RACE against the deadline, not only an abort — work that
//      ignores its abort is released anyway; the release is in a `finally`
//      around the `await`, not a statement after it; a request that
//      arrives while one is in flight is remembered, not dropped, and
//      collapses with every other into exactly ONE further attempt.

import { RESEED_TIMEOUT_MS } from "./readingFreshness";

/** The rate limit on acted-on wakes. A thawing phone fires
 *  `visibilitychange`, Capacitor `appStateChange` and `pageshow` within a
 *  second of each other and they all mean the same thing.
 *
 *  ⚠ Measured from the START of an acted-on wake, not from the completion
 *  of the last one. Measuring from completion is what let a slow reseed
 *  widen the gap without bound. */
export const WAKE_MIN_GAP_MS = 1_500;

export interface ReseedRunnerDeps {
  /** The work. MUST pass the controller's signal to everything it awaits —
   *  an abort that only stops the dispatch leaves the fetch pending and the
   *  guard held, which is the defect this file exists to close. */
  reseed: (ctrl: AbortController) => Promise<void>;
  timeoutMs?: number;
  setTimeout?: (fn: () => void, ms: number) => number;
  clearTimeout?: (id: number) => void;
  makeController?: () => AbortController;
  /** Called once per attempt that hit the timeout. */
  onTimeout?: () => void;
}

export interface ReseedRunner {
  /** Ask for a reseed. Never throws, never awaited by the caller. */
  request(): void;
  /** Is an attempt in flight right now? This is the guard, and the only
   *  place it is readable from. */
  inFlight(): boolean;
  /** Is a further attempt queued behind the one in flight? */
  pending(): boolean;
  /** How many attempts have been STARTED since the runner was created. */
  attempts(): number;
  /** How many of them hit the timeout. */
  timeouts(): number;
  /** Unmount. Aborts anything in flight — which settles its `await`, whose
   *  `finally` releases the guard — and refuses further work. */
  dispose(): void;
}

export function createReseedRunner(deps: ReseedRunnerDeps): ReseedRunner {
  const timeoutMs = deps.timeoutMs ?? RESEED_TIMEOUT_MS;
  const setT = deps.setTimeout ?? ((fn, ms) => globalThis.setTimeout(fn, ms) as unknown as number);
  const clearT = deps.clearTimeout ?? ((id) => globalThis.clearTimeout(id));
  const makeCtrl = deps.makeController ?? (() => new AbortController());

  let ctrl: AbortController | null = null;
  let queued = false;
  let disposed = false;
  let attempts = 0;
  let timeouts = 0;

  async function loop(): Promise<void> {
    for (;;) {
      if (disposed) return;
      queued = false;
      attempts += 1;
      const c = makeCtrl();
      ctrl = c;
      let timedOut = false;
      // ⚠ THE BOUND IS A RACE, NOT ONLY AN ABORT. Aborting is right and is
      // done — but this whole cycle exists because a fetch iOS froze
      // mid-flight did not behave, and an abort only ends the wait if the
      // work honours it. Racing the work against the deadline means the
      // release below happens within `timeoutMs` whatever the work does.
      // (The cycle-433 reviewers found the same asymmetry in the token
      // refresh, where it was a VIOLATION; this is its twin.)
      //
      // ⚠ AN ABANDONED ATTEMPT DISPATCHES NOTHING, and the ordering is what
      // makes that true: the timer aborts the controller BEFORE it resolves
      // the deadline, and `reseedFromRest`'s `if (signal.aborted) return`
      // guard dominates a dispatch block with no `await` in it — so a
      // macrotask cannot interleave, and the attempt either dispatched
      // everything before the deadline or dispatches nothing at all. ⚠ A
      // future cycle that inserts an `await` after that guard breaks this;
      // `readingUnknown.test.ts` pins the block's synchronicity for that
      // reason.
      //
      // The cost, stated: during a freeze in which the work ignores its
      // abort, one abandoned set of four REST reads is left behind per
      // bound. That is the deliberate trade — the guard MUST release — and
      // it is bounded at one set per `RESEED_TIMEOUT_MS`.
      let passDeadline: () => void = () => {};
      const deadline = new Promise<void>((resolve) => {
        passDeadline = resolve;
      });
      const timer = setT(() => {
        timedOut = true;
        c.abort();
        passDeadline();
      }, timeoutMs);
      try {
        await Promise.race([
          // A reseed is best-effort: a rejection must not reject the race,
          // and must not skip the release below.
          deps.reseed(c).catch(() => {}),
          deadline,
        ]);
      } catch {
        // ⚠ THE OUTER CATCH IS NOT REDUNDANT. The inner `.catch` covers a
        // REJECTED promise; this covers a SYNCHRONOUS throw out of
        // `deps.reseed(c)` (or a non-thenable return, which makes that
        // `.catch` itself a TypeError). Without it such a throw escapes the
        // `try`, skips the queued-wake handling below, and rejects
        // `loop()` at its `void` call site. Unreachable with today's async
        // `reseedFromRest` — but the `ReseedRunnerDeps.reseed` contract
        // admits it, and the guarantee this file's header makes is
        // unconditional.
      } finally {
        // ⚠ THE RELEASE IS HERE, IN A `finally` AROUND THE `await`, AND
        // NOWHERE ELSE. Not a statement after the await (an early return or
        // a throw skips it) and not a `.then` (an abort rejects).
        clearT(timer);
        ctrl = null;
      }
      if (timedOut) {
        timeouts += 1;
        deps.onTimeout?.();
      }
      if (!queued || disposed) return;
    }
  }

  return {
    request(): void {
      if (disposed) return;
      if (ctrl != null) {
        // ⚠ COALESCE, NEVER DISCARD. The wake that arrives during a hung
        // reseed is the one that matters most.
        queued = true;
        return;
      }
      void loop();
    },
    inFlight: () => ctrl != null,
    pending: () => queued,
    attempts: () => attempts,
    timeouts: () => timeouts,
    dispose(): void {
      disposed = true;
      queued = false;
      ctrl?.abort();
    },
  };
}

export interface WakeHandlerDeps {
  now: () => number;
  /** Synchronous, unconditional, behind no guard. */
  reopen: () => void;
  /** Coalescing and bounded — normally `runner.request`. */
  reseed: () => void;
  minGapMs?: number;
}

export interface WakeHandler {
  /** One wake signal. */
  wake(): void;
  /** How many wakes were acted on (as opposed to rate-limited away). */
  acted(): number;
}

/** ⚠ EVERY ACTED-ON WAKE RECONNECTS. The reconnect is not conditional on
 *  the reseed, on `readyState`, or on anything else — a socket that
 *  survived a freeze is exactly the one that lies (cycle 429), and a reseed
 *  that is hung is exactly when a reconnect matters most (cycle 433). */
export function createWakeHandler(deps: WakeHandlerDeps): WakeHandler {
  const gap = deps.minGapMs ?? WAKE_MIN_GAP_MS;
  let lastAt = Number.NEGATIVE_INFINITY;
  let acted = 0;
  return {
    wake(): void {
      const now = deps.now();
      if (now - lastAt < gap) return;
      lastAt = now;
      acted += 1;
      deps.reopen();
      deps.reseed();
    },
    acted: () => acted,
  };
}
